import styled from '@emotion/styled';
import { Modal } from 'antd';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import studyingIcon from 'src/public/img/undraw-studying-s-3-l-7.svg';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import * as S from 'src/modules/programs/stepComponents/styles';
import {
    fetchProgramSurvey,
    submitProgramSurvey,
} from 'src/modules/programs/surveys/actions/surveysActions';
import { QuestionsWrapper, QuestionWrapper } from 'src/modules/programs/surveys/components/styles';
import { SurveyQuestionSwitch } from 'src/modules/programs/surveys/components/SurveyQuestionSwitch';
import {
    GenericSurveyAnswer,
    GenericSurveyQuestion,
} from 'src/modules/programs/surveys/models/surveys';
import {
    getProgramSurveyAnswers,
    getProgramSurveyQuestionAnswerByRenderId,
    getProgramSurveyQuestionByRenderId,
    isProgramSurveyAnswered,
} from 'src/modules/programs/surveys/selectors/surveysSelectors';
import { Button } from 'src/shared/components/Button';
import { color, font, layout } from 'src/shared/styles/variables';

import { CBTProgramWeekTwoSurvey } from '../models/weekTwo';

const surveyId = CBTProgramWeekTwoSurvey.WeighingItUp;

const StyledModal = styled(Modal)({
    '& .ant-modal-content': {
        maxWidth: layout.modalWidth.small,
        margin: '0 auto',
        textAlign: 'center',
    },
});

interface ModalIconProps {
    iconUrl: string;
}

const ModalIcon = styled('i')<ModalIconProps>(props => ({
    display: 'block',
    backgroundImage: `url(${props.iconUrl})`,
    height: '10.50rem',
    width: '10.50rem',
    margin: '0 auto 1.75rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const ModalTitle = styled('h3')(
    {
        width: '100%',
        margin: '0rem 0rem 1.31rem',
        color: color.bayOfMany,
    },
    font.heavy(font.size.extraLarge),
);

const ModalQuestionWrapper = styled.div({
    marginBottom: '0.88rem',
});

interface StateProps {
    motivationDescriptionQuestion?: GenericSurveyQuestion;
    motivationRateQuestion?: GenericSurveyQuestion;
    motivationRateReasonQuestion?: GenericSurveyQuestion;
    answers: GenericSurveyAnswer[] | undefined;
    motivationRateAnswer: GenericSurveyAnswer | undefined;
    isSurveyAnswered: boolean;
}

interface ActionsProps {
    fetchProgramSurvey: typeof fetchProgramSurvey;
    submitProgramSurvey: typeof submitProgramSurvey;
}

interface Props extends StepButtonsProps, StateProps, ActionsProps, WithTranslation {
    programId: ProgramEnum;
}

interface State {
    isModalOpen: boolean;
}

class WeighingItUpStepContainer extends React.Component<Props, State> {
    public state: State = {
        isModalOpen: false,
    };

    public componentDidMount(): void {
        const { programId } = this.props;

        this.props.fetchProgramSurvey({
            programId,
            surveyId,
        });
    }

    public handleToggleModal = () => {
        this.setState(state => ({ isModalOpen: !state.isModalOpen }));
    };

    private handleSubmit = () => {
        const { programId, answers, goToNextStep } = this.props;

        if (!answers) {
            return;
        }

        this.props.submitProgramSurvey({
            programId,
            surveyId,
            answers,
        });

        goToNextStep();
    };

    public render(): JSX.Element {
        const {
            programId,
            isSurveyAnswered,
            goToPreviousStep,
            motivationDescriptionQuestion,
            motivationRateQuestion,
            motivationRateReasonQuestion,
            motivationRateAnswer,
            t,
        } = this.props;

        const commonQuestionProps = { programId, surveyId, noShadow: true, noPadding: true };

        const rateAnswer = motivationRateAnswer && motivationRateAnswer.data.content;

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading>{t('week-2.weighing-it-up.title')}</S.StepHeading>
                        <QuestionsWrapper>
                            <QuestionWrapper>
                                <SurveyQuestionSwitch
                                    {...commonQuestionProps}
                                    question={motivationDescriptionQuestion}
                                />
                            </QuestionWrapper>
                            <QuestionWrapper>
                                <SurveyQuestionSwitch
                                    {...commonQuestionProps}
                                    question={motivationRateQuestion}
                                />
                            </QuestionWrapper>
                        </QuestionsWrapper>
                    </S.ContentColumn>
                </S.ContentRow>
                <StyledModal
                    closable={false}
                    visible={this.state.isModalOpen}
                    onCancel={this.handleToggleModal}
                    footer={null}
                    width="auto"
                    centered
                >
                    <ModalIcon iconUrl={studyingIcon} />
                    <ModalTitle>
                        {this.props.t('week-2.weighing-it-up.modalTitle', { rateAnswer })}
                    </ModalTitle>
                    <ModalQuestionWrapper>
                        <SurveyQuestionSwitch
                            {...commonQuestionProps}
                            question={motivationRateReasonQuestion}
                        />
                    </ModalQuestionWrapper>
                    <Button onClick={this.handleSubmit}>Continue</Button>
                </StyledModal>
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={this.handleToggleModal}
                    nextDisabled={!isSurveyAnswered}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId }: Props): StateProps => {
    const surveyRelatedArgs = [state, programId, surveyId] as any;

    return {
        motivationDescriptionQuestion: getProgramSurveyQuestionByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'motivationDescription',
        ]),
        motivationRateQuestion: getProgramSurveyQuestionByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'motivationRate',
        ]),
        motivationRateReasonQuestion: getProgramSurveyQuestionByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'motivationRateReason',
        ]),
        answers: getProgramSurveyAnswers(state, programId, surveyId),
        motivationRateAnswer: getProgramSurveyQuestionAnswerByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'motivationRate',
        ]),
        isSurveyAnswered: isProgramSurveyAnswered(state, programId, surveyId),
    };
};

export const WeighingItUpStep = withTranslation('cbt')(
    connect<StateProps, ActionsProps>(mapStateToProps, {
        fetchProgramSurvey,
        submitProgramSurvey,
    })(WeighingItUpStepContainer),
);
